import {
  HARDENING_STATUS_LABEL,
  type HardeningServer,
  type HardeningStatus,
  type HardeningTaskView,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { getHardening, setHardeningAuto } from '../api/hardening.js';
import { useCurrentUser } from '../auth/session-context.js';
import { useReauth } from '../auth/reauth-context.js';
import { ApplyFixesDialog } from '../components/ApplyFixesDialog.js';
import { PageHeader } from '../components/PageHeader.js';
import { formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Server tasks: the fixes Bartizo makes on a server, and where each one stands on
 * each — done, running, waiting for a reboot, or something only a person can do.
 * The state is read from the benchmark's checks, the last command and the server's
 * own reboot flag, so "Done" means the check now passes. Making a fix is the
 * Compliance page's dialog, which asks an admin for their password again.
 */

const BADGE: Readonly<Record<HardeningStatus, string>> = {
  done: 'badge-success',
  running: 'badge-accent',
  checking: 'badge-accent',
  needs_reboot: 'badge-warning',
  needs_you: 'badge-danger',
  not_applied: 'badge-muted',
  unknown: 'badge-muted',
};

/** The states a fix can still change: everything but done and one in flight. */
const CAN_APPLY: ReadonlySet<HardeningStatus> = new Set(['not_applied', 'needs_you']);

function Apply({
  task,
  server,
  label,
}: {
  task: HardeningTaskView;
  server?: HardeningServer;
  label: string;
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant={server === undefined ? 'primary' : 'secondary'}
        onClick={() => {
          void ensureReauthenticated().then((ok) => {
            if (ok) setOpen(true);
          });
        }}
      >
        {label}
      </Button>
      {open && (
        <ApplyFixesDialog
          choices={[{ id: task.id, title: task.label, does: task.does, checks: 1 }]}
          scope={server === undefined ? {} : { server: server.id }}
          scopeName={server === undefined ? 'every server' : server.name}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

/** Whether the task applies itself to servers that never had it tried; an admin's switch. */
function AutoSwitch({ task }: { task: HardeningTaskView }): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (enabled: boolean) => setHardeningAuto(task.id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hardening'] }),
  });
  return (
    <label className="row text-sm">
      <input
        type="checkbox"
        checked={task.auto.enabled}
        disabled={change.isPending}
        onChange={(event) => {
          const enabled = event.currentTarget.checked;
          void ensureReauthenticated().then((ok) => {
            if (ok) change.mutate(enabled);
          });
        }}
      />
      <span>
        Apply automatically to servers that have never had it, a few at a time. A failure is never
        retried: it waits for you.
        {task.auto.enabled && task.auto.byEmail !== null && (
          <span className="cell-sub">Switched on by {task.auto.byEmail}.</span>
        )}
      </span>
    </label>
  );
}

function TaskCard({ task, isAdmin }: { task: HardeningTaskView; isAdmin: boolean }): ReactNode {
  const columns: Column<HardeningServer>[] = [
    {
      key: 'server',
      header: 'Server',
      rowHeader: true,
      cell: (row) => <Link to={`/servers/${encodeURIComponent(row.id)}`}>{row.name}</Link>,
      sortValue: (row) => row.name,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <>
          <span className={`badge ${BADGE[row.status]}`}>{HARDENING_STATUS_LABEL[row.status]}</span>
          {row.detail !== undefined && <span className="cell-sub">{row.detail}</span>}
          {row.status === 'needs_you' && (
            <span className="cell-sub">
              Bartizo could not finish this one. Follow the steps on the Compliance page for this
              check, or record a decision there.
            </span>
          )}
          {row.status === 'needs_reboot' && (
            <span className="cell-sub">
              Restart this server to finish; the notice clears itself.
            </span>
          )}
        </>
      ),
    },
    {
      key: 'tried',
      header: 'Last tried',
      wideOnly: true,
      cell: (row) =>
        row.lastTriedAt === undefined ? (
          <span className="muted">never</span>
        ) : (
          formatRelative(row.lastTriedAt)
        ),
    },
    ...(isAdmin
      ? [
          {
            key: 'apply',
            header: '',
            align: 'end' as const,
            cell: (row: HardeningServer) =>
              CAN_APPLY.has(row.status) ? (
                <Apply
                  task={task}
                  server={row}
                  label={row.status === 'needs_you' ? 'Try again' : 'Apply'}
                />
              ) : null,
          },
        ]
      : []),
  ];
  const todo = task.counts.not_applied;
  return (
    <Card
      title={task.label}
      description={`${task.does}.${task.restart === 'reboot' ? ' Needs a restart afterwards.' : ''}`}
      aside={
        <span className="badge-list">
          {task.counts.needs_you > 0 && (
            <span className="badge badge-danger">{task.counts.needs_you} need you</span>
          )}
          {task.counts.needs_reboot > 0 && (
            <span className="badge badge-warning">{task.counts.needs_reboot} need a reboot</span>
          )}
          <span className="badge badge-success">{task.counts.done} done</span>
        </span>
      }
      footer={
        isAdmin ? (
          <div className="stack">
            {todo > 0 && (
              <Apply
                task={task}
                label={`Apply to the ${String(todo)} server${todo === 1 ? '' : 's'} that need it`}
              />
            )}
            <AutoSwitch task={task} />
          </div>
        ) : task.auto.enabled ? (
          <span className="muted text-sm">Applies itself to servers that have never had it.</span>
        ) : undefined
      }
    >
      <DataTable
        caption={task.label}
        columns={columns}
        rows={task.servers}
        rowKey={(row) => row.id}
        empty={<p className="muted text-sm">No servers yet.</p>}
      />
    </Card>
  );
}

export function TasksPage(): ReactNode {
  const isAdmin = useCurrentUser().user.role === 'admin';
  const query = useQuery({
    queryKey: ['hardening'],
    queryFn: getHardening,
    refetchInterval: 30_000,
  });
  return (
    <>
      <PageHeader
        title="Server tasks"
        description="The fixes Bartizo can make for you, and where each stands on every server. Done means the benchmark's check now passes there. A server that needs a restart or a person's hands is listed first."
      />
      {query.isPending && <Loading />}
      {query.isError && (
        <Alert tone="danger" title="Could not read the tasks">
          Try again in a moment.
        </Alert>
      )}
      {query.data?.tasks.map((task) => (
        <TaskCard key={task.id} task={task} isAdmin={isAdmin} />
      ))}
    </>
  );
}
